<script setup lang="ts">
import { computed, ref } from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import { Job, jobsStore } from "@/store/jobs";
import { JobStatus } from "@/models/etc/types";

const props = defineProps<{
  job: Job;
}>();

const emit = defineEmits<{
  (e: "select-job", job: Job): void;
}>();

const store = jobsStore();
const isLoading = ref(false);

const onCardClick = () => {
  emit("select-job", props.job);
};

const showUserJobs = (userId: number) => {
  store.setUserForUpdateJobsList(userId);
};

const getJobStatusBarColor = (status: JobStatus) => {
  switch (status) {
    case JobStatus.Standard:
      return "#9A9C9E";
    case JobStatus.Working:
      return "#86CED2";
    case JobStatus.WaitingForCheck:
      return "#FFE7AA";
    case JobStatus.Completed:
      return "#6DA279";
    case JobStatus.Lost:
      return "#EE966A";
  }
};

const formatDate = (date?: string | Date) => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const loaderStyle = computed(() => ({
  "background-color": "rgba(255, 255, 255, 0.5)",
}));

const additionsExpanded = ref(false);

const formatDateWithTime = (date?: string | Date) => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};
</script>

<template>
  <div
    ref="$job"
    :class="{
      'jobs-panel-list-item': true,
    }"
    @click="onCardClick"
  >
    <app-loader v-if="isLoading" :style="loaderStyle" />
    <div
      :class="{
        'jobs-panel-list-item__drop-zone': true,
      }"
    />
    <div
      class="jobs-panel-list-item__colored"
      :style="{ backgroundColor: getJobStatusBarColor(props.job.Status) }"
    />
    <div class="jobs-panel-list-item__header">
      <div class="jobs-panel-list-item__header-workers">
        <!-- <div
          class="jobs-panel-list-item__header-worker"
          @click="showUserJobs(props.job.Worker.Id)"
        > -->
        <div class="jobs-panel-list-item__header-worker">
          {{ props.job.Worker.Name }}
        </div>
        <template v-if="props.job.Creator">
          <div class="jobs-panel-list-item__header-divider">
            <app-icon
              class="jobs-panel-list-item__header-options-btn"
              name="chevron-left"
              width="16px"
              color="#499259"
            />
          </div>
          <!-- <div
            class="jobs-panel-list-item__header-worker"
            @click="showUserJobs(props.job.Creator.Id)"
          > -->
          <div class="jobs-panel-list-item__header-worker">
            {{ props.job.Creator.Name }}
          </div>
        </template>
      </div>
    </div>
    <div class="jobs-panel-list-item__body">
      <div
        class="jobs-panel-list-item__body-message"
        v-html="props.job.Message"
      />
    </div>
    <div class="jobs-panel-list-item__footer">
      <div class="jobs-panel-list-item__footer-bottom">
        <div
          :class="{
            'jobs-panel-list-item__footer-bottom-date': true,
            'jobs-panel-list-item__footer-bottom-date--lost': !props.job.CompletionDate && props.job.Lost,
            'jobs-panel-list-item__footer-bottom-date--success': !!props.job.CompletionDate,
          }"
          :title="props.job.CompletionDate ? 'Завершена' : 'Срок'"
        >
          <app-icon
            v-if="props.job.CompletionDate || props.job.ToDoDate"
            class="jobs-panel-list-item__footer-bottom-date-clock"
            name="clock"
            width="16px"
            :title="props.job.CompletionDate ? 'Завершена' : 'Срок'"
          />
          <template v-if="props.job.CompletionDate">
            {{ formatDateWithTime(props.job.CompletionDate) }}
          </template>
          <template v-else-if="props.job.ToDoDate">
            {{ formatDate(props.job.ToDoDate) }}
          </template>
        </div>
        {{ formatDate(props.job.CreateTime) }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/styles/color.scss";

.jobs-panel-list-item {
  display: flex;
  flex-direction: column;
  position: relative;
  // padding: 0 8px 0 16px;
  padding: 0 0px 0 8px;
  color: #4d4d4d;
  border: 1px dashed transparent;
  border-left: none !important;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: $color-divider;
  }

  &__colored {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-top-left-radius: 8px 12px;
    border-bottom-left-radius: 8px 12px;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 4px;

    &-workers {
      display: flex;
      align-items: center;
      min-width: 0;
      color: #438451;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      overflow: hidden;
    }

    &-worker {
      display: block;
      cursor: pointer;
      white-space: nowrap;
      flex-shrink: 0;

      &:last-child {
        flex-shrink: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &-divider {
      display: flex;
      flex-shrink: 0;
      align-items: center;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    margin-bottom: 4px;

    &-message {
      font-size: 12px;
      line-height: 16px;
      color: $color-text-main;
      margin-bottom: 2px;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &-title {
      font-weight: 500;
      color: #4d4d4d;
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    max-width: 100%;

    &-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
      // margin-top: 4px;
      font-size: 12px;
      line-height: 16px;
      color: #979797;

      &-date {
        color: #979797;
        font-size: 12px;
        line-height: 16px;

        &--lost {
          color: #ee966a !important;

          svg {
            path {
              color: #ee966a !important;
            }
          }
        }

        &--success {
          color: #6da279 !important;

          svg {
            path {
              color: #6da279 !important;
            }
          }
        }

        &-clock {
          cursor: auto;
          margin-top: -2px;
          margin-right: 4px;

          &:hover {
            background: transparent;
          }
        }
      }
    }
  }
}
</style>
